import { motion } from "motion/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useInterval } from "~/utils/use-interval";

const quotes = [
  {
    image: "https://i.pravatar.cc/80?img=1",
    text: `"Saying 'just center it horizontally' is the fastest way to make a frontend developer cry."`,
    name: "Chris Borders",
    roleCompany: "UI/UX Designer at NetscapeGoat",
  },
  {
    image: "https://i.pravatar.cc/80?img=2",
    text: `"JavaScript is like a toddler. Sometimes brilliant, sometimes throwing errors for no reason."`,
    name: "Elon Musketeer",
    roleCompany: "Chief Meme Officer at SpaceFlexbox",
  },
  {
    image: "https://i.pravatar.cc/80?img=4",
    text: `"I told my browser to embrace its errors. Now it's a motivational speaker."`,
    name: "Krisina Boxmodel",
    roleCompany: "Lead spacing engineer at Margin Collapse Inc.",
  },
  {
    image: "https://i.pravatar.cc/80?img=5",
    text: `"Why did the CSS go to therapy? Because it couldn't deal with its parent issues!"`,
    name: "Sarah Overflow",
    roleCompany: "Senior Flexbox Therapist at CSS Grid Clinic",
  },
];

export const VariantsAnimation = () => {
  const getQuoteVariant = (index: number, suffix?: "highlighted") =>
    `quote_${index}` + (suffix ? `_${suffix}` : "");

  const [activeQuoteIndex, setActiveQuoteIndex] = useState(0);
  const lastQuoteIndex = quotes.length - 1;
  const [isPaused, setIsPaused] = useState(false);
  const speed = 4000;

  const next = () => {
    setActiveQuoteIndex((currentQuote) =>
      currentQuote >= lastQuoteIndex ? 0 : currentQuote + 1,
    );
  };

  const previous = () => {
    setActiveQuoteIndex((currentQuote) =>
      currentQuote === 0 ? lastQuoteIndex : currentQuote - 1,
    );
  };

  useInterval(
    () => {
      // See note in the lesson on why we use requestAnimationFrame here.
      window.requestAnimationFrame(next);
    },
    isPaused ? null : speed,
  );

  return (
    <motion.div
      animate={getQuoteVariant(activeQuoteIndex)}
      onPointerEnter={() => setIsPaused(true)}
      onPointerLeave={() => setIsPaused(false)}
      whileHover={["paused", getQuoteVariant(activeQuoteIndex, "highlighted")]}
      initial="inactive"
      className="relative mx-auto w-[400px] max-w-full rounded-2xl bg-white px-8 py-6 text-black"
    >
      <div className="flex justify-center">
        {quotes.map((quote, index) => (
          <motion.img
            key={index}
            variants={{
              paused: { filter: "grayscale(1)", scale: 0.9 },
              [getQuoteVariant(index)]: { scale: 1.2, zIndex: 10 },
              [getQuoteVariant(index, "highlighted")]: {
                filter: "grayscale(0)",
                scale: 1.2,
              },
            }}
            className="relative -mx-2 h-8 w-8 rounded-full border border-black"
            src={quote.image}
          />
        ))}
      </div>
      <div className="mt-6 grid text-center leading-[1.3]">
        {quotes.map((quote, index) => (
          <motion.blockquote
            key={index}
            variants={{
              [getQuoteVariant(index)]: { y: 0, opacity: 1 },
              inactive: { y: 20, opacity: 0 },
            }}
            className="relative [grid-area:1/1]"
          >
            <p>{quote.text}</p>
            <p className="mt-3 text-sm font-bold">
              {quote.name} - {quote.roleCompany}
            </p>
          </motion.blockquote>
        ))}
      </div>
      <motion.button
        onClick={previous}
        variants={{ paused: { x: 0, opacity: 1 } }}
        initial={{ x: -20, opacity: 0 }}
        className="absolute left-0 top-1/2 block px-3"
      >
        <ChevronLeft /> <span className="sr-only">Previous</span>
      </motion.button>
      <motion.button
        onClick={next}
        variants={{
          paused: { x: 0, opacity: 1 },
        }}
        initial={{ x: 20, opacity: 0 }}
        className="absolute right-0 top-1/2 block px-3"
      >
        <ChevronRight /> <span className="sr-only">Next</span>
      </motion.button>
    </motion.div>
  );
};
